import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { NotyService } from '@app/services';
import { AppDropdown } from '@app/shared/dropdown/dropdown';

interface DropdownGeneratorOption {
  id: number;
  elementType: DropdownOptionElement;
  text: string;
  description: string;
  hasDescription: boolean;
  descriptionColor: DropdownItemColor;
  hasDescriptionBorderBottom: boolean;
  hasIcon: boolean;
  iconColor: DropdownIconColor;
  itemColor: DropdownItemColor;
  fontWeight: DropdownItemFontWeight;
  hasSwitch: boolean;
  hasDivider: boolean;
  hasArrow: boolean;
  switchValue: boolean;
}

type DropdownClosedMenuStyle = '' | 'filled-blue' | 'filled-grey' | 'filled-white' | 'corner';
type DropdownClosedMenuSize = '' | 'compact';
type DropdownClosedMenuIcon = '' | 'chevron' | 'ellipsis';
type DropdownColorShade = '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | '950';
type DropdownClosedMenuTextColorFamily = 'primary' | 'autentigrey';
type DropdownClosedMenuTextColor = `${DropdownClosedMenuTextColorFamily}-${DropdownColorShade}`;
type DropdownClosedMenuIconFontSize = '10' | '12' | '14' | '16' | '18' | '20' | '24';
type DropdownOptionElement = 'button' | 'anchor';
type DropdownIconColor = DropdownColorShade;
type DropdownItemColor = 'text-l2' | 'text-l1' | 'danger';
type DropdownItemFontWeight = '300' | '400' | '500' | '600' | '700';

@Component({
  selector: 'app-dropdown-generator',
  templateUrl: './dropdown-generator.component.html',
  styleUrls: ['./dropdown-generator.component.scss'],
  standalone: false
})
export class DropdownGeneratorComponent implements OnInit, AfterViewInit {
  @ViewChild(AppDropdown) dropdown: AppDropdown;

  readonly defaultIcon = 'fa-regular fa-cloud-download-alt';

  closedMenuTitle = 'Dropdown';
  closedMenuIcon: DropdownClosedMenuIcon = 'chevron';
  closedMenuTextColor: DropdownClosedMenuTextColor = 'primary-600';
  closedMenuIconFontSize: DropdownClosedMenuIconFontSize = '14';
  closedMenuStyle: DropdownClosedMenuStyle = '';
  closedMenuSize: DropdownClosedMenuSize = '';
  hasDescription = false;
  descriptionColor: DropdownItemColor = 'text-l2';
  hasDescriptionBorderBottom = false;
  hasIcon = true;
  iconColor: DropdownIconColor = '400';
  optionElement: DropdownOptionElement = 'button';
  itemColor: DropdownItemColor = 'text-l1';
  itemFontWeight: DropdownItemFontWeight = '400';
  hasSwitch = false;
  hasArrow = false;
  switchValue = false;
  options: DropdownGeneratorOption[] = [];
  htmlCode = '';

  private nextOptionId = 1;
  private readonly defaultClosedMenuIconFontSize: DropdownClosedMenuIconFontSize = '14';
  private readonly defaultClosedMenuTextColor: DropdownClosedMenuTextColor = 'primary-600';
  private readonly defaultDescriptionColor: DropdownItemColor = 'text-l2';
  private readonly defaultIconColor: DropdownIconColor = '400';
  private readonly defaultItemColor: DropdownItemColor = 'text-l1';
  private readonly defaultItemFontWeight: DropdownItemFontWeight = '400';

  constructor(private notyService: NotyService) {}

  ngOnInit() {
    this.updateDropdownCode();
  }

  ngAfterViewInit() {
    this.keepPreviewOpen();
  }

  addOption() {
    const option: DropdownGeneratorOption = {
      id: this.nextOptionId++,
      elementType: this.hasSwitch ? 'button' : this.optionElement,
      text: 'textOption',
      description: 'descriptionText',
      hasDescription: this.hasDescription,
      descriptionColor: this.hasDescription ? this.descriptionColor : 'text-l2',
      hasDescriptionBorderBottom: this.hasDescription && this.hasDescriptionBorderBottom,
      hasIcon: this.hasIcon,
      iconColor: this.hasIcon ? this.iconColor : '400',
      itemColor: this.itemColor,
      fontWeight: this.itemFontWeight,
      hasSwitch: this.hasSwitch,
      hasDivider: false,
      hasArrow: this.hasArrow && !this.hasSwitch,
      switchValue: this.switchValue
    };

    this.options = [...this.options, option];
    this.updateDropdownCode();
  }

  addDivider() {
    const option: DropdownGeneratorOption = {
      id: this.nextOptionId++,
      elementType: 'button',
      text: 'HR',
      description: '',
      hasDescription: false,
      descriptionColor: 'text-l2',
      hasDescriptionBorderBottom: false,
      hasIcon: false,
      iconColor: '400',
      itemColor: 'text-l1',
      fontWeight: '300',
      hasSwitch: false,
      hasDivider: true,
      hasArrow: false,
      switchValue: false
    };

    this.options = [...this.options, option];
    this.updateDropdownCode();
  }

  removeOption(optionId: number) {
    this.options = this.options.filter(option => option.id !== optionId);
    this.updateDropdownCode();
  }

  clearOptions() {
    this.options = [];
    this.updateDropdownCode();
  }

  updateDropdownCode() {
    this.htmlCode = this.generateDropdownHTML();
    this.keepPreviewOpen();
  }

  copySuccess() {
    this.notyService.success('Código copiado com sucesso.', 2000);
  }

  getOptionClasses(option: DropdownGeneratorOption) {
    return this.getGeneratedOptionClasses(option);
  }

  getDescriptionColorClass(descriptionColor: DropdownItemColor) {
    if (descriptionColor === this.defaultDescriptionColor) {
      return '';
    }

    return `dropdown__item-description--${descriptionColor}`;
  }

  submenuId(option: DropdownGeneratorOption) {
    return `stylesheet-dropdown-submenu-${option.id}`;
  }

  get closedMenuStyleClass() {
    if (this.closedMenuStyle === 'filled-blue') {
      return 'dropdown__toggle-control--filled-blue';
    }

    if (this.closedMenuStyle === 'filled-grey') {
      return 'dropdown__toggle-control--filled-grey';
    }

    if (this.closedMenuStyle === 'filled-white') {
      return 'dropdown__toggle-control--filled-white';
    }

    if (this.closedMenuStyle === 'corner') {
      return 'dropdown__toggle-control--corner';
    }

    return '';
  }

  get hasClosedMenuIcon() {
    return this.closedMenuIcon !== '';
  }

  getClosedMenuTextColorClass(closedMenuTextColor: DropdownClosedMenuTextColor) {
    if (closedMenuTextColor === this.defaultClosedMenuTextColor) {
      return '';
    }

    return `dropdown__toggle-control--text-${closedMenuTextColor}`;
  }

  getClosedMenuIconFontSizeClass(closedMenuIconFontSize: DropdownClosedMenuIconFontSize) {
    if (closedMenuIconFontSize === this.defaultClosedMenuIconFontSize) {
      return '';
    }

    return `dropdown__toggle-icon--font-size-${closedMenuIconFontSize}`;
  }

  get closedMenuSizeClass() {
    if (this.closedMenuSize === 'compact') {
      return 'dropdown__toggle-control--compact';
    }

    return '';
  }

  getClosedMenuToggleClasses() {
    return [this.closedMenuStyleClass, this.closedMenuSizeClass, this.getClosedMenuTextColorClass(this.closedMenuTextColor)].filter(Boolean).join(' ');
  }

  getClosedMenuIconClasses() {
    return this.getClosedMenuIconFontSizeClass(this.closedMenuIconFontSize);
  }

  private generateOptionHTML(option: DropdownGeneratorOption) {
    if (option.hasDivider) {
      return '<hr class="dropdown-divider" />';
    }

    const optionClasses = this.getGeneratedOptionClasses(option);
    const classAttribute = optionClasses.length ? ` class="${optionClasses.join(' ')}"` : '';
    const iconMarkup = option.hasIcon ? `<i class="${this.defaultIcon}"></i>` : '';
    const labelMarkup = this.generateLabelMarkup(option);

    if (option.hasSwitch) {
      return `<label class="${['dropdown__item-switch', ...optionClasses].join(' ')}">
  ${[iconMarkup, this.generateSwitchLabelMarkup(option), '<app-switch [(ngModel)]="value"></app-switch>'].filter(Boolean).join('\n  ')}
</label>`;
    }

    if (option.hasArrow) {
      return `${this.generateOptionOpeningTag(option, classAttribute, this.submenuId(option))}
  ${[iconMarkup, labelMarkup].filter(Boolean).join('\n  ')}
${this.generateOptionClosingTag(option)}`;
    }

    return `${this.generateOptionOpeningTag(option, classAttribute)}
  ${[iconMarkup, labelMarkup].filter(Boolean).join('\n  ')}
${this.generateOptionClosingTag(option)}`;
  }

  private generateDropdownHTML() {
    const toggleClass = ['dropdown__toggle-control', this.closedMenuStyleClass, this.closedMenuSizeClass, this.getClosedMenuTextColorClass(this.closedMenuTextColor)].filter(Boolean);

    if (this.closedMenuTitle && this.hasClosedMenuIcon) {
      toggleClass.push('dropdown__toggle-control--icon-end');
    } else if (!this.closedMenuTitle && this.hasClosedMenuIcon) {
      toggleClass.push('dropdown__toggle-control--icon-only');
    }

    const toggleContent = [this.closedMenuTitle ? `<span>${this.escapeHtml(this.closedMenuTitle)}</span>` : '', this.getClosedMenuIconMarkup()].filter(Boolean);
    const menuContent = this.options.length ? this.options.map(option => this.generateOptionHTML(option)).join('\n\n') : '  <!-- Adicione options aqui -->';
    const submenuContent = this.options
      .map(option => this.generateSubmenuHTML(option))
      .filter(Boolean)
      .join('\n\n');

    return `<div appDropdown placement="bottom-right">
  <button type="button" class="${toggleClass.join(' ')}" appDropdownToggle>
    ${toggleContent.join('\n    ')}
  </button>

  <div appDropdownMenu>
${this.indent(menuContent, 4)}
  </div>
${submenuContent ? `\n${this.indent(submenuContent, 2)}` : ''}
</div>`;
  }

  private generateSubmenuHTML(option: DropdownGeneratorOption) {
    if (!option.hasArrow) {
      return '';
    }

    return `<div appDropdownSubmenu="${this.submenuId(option)}">
  <button type="button" appDropdownBackButton>Voltar</button>
  <button type="button" appDropdownItem>Opção interna</button>
</div>`;
  }

  private generateLabelMarkup(option: DropdownGeneratorOption) {
    if (!option.hasDescription) {
      return '{{ textOption }}';
    }

    const descriptionClasses = ['dropdown__item-description', this.getDescriptionColorClass(option.descriptionColor)].filter(Boolean).join(' ');

    return `<div>
    <span class="dropdown__item-title">{{ textOption }}</span>
    <span class="${descriptionClasses}">{{ descriptionText }}</span>
  </div>`;
  }

  private generateSwitchLabelMarkup(option: DropdownGeneratorOption) {
    if (option.hasDescription) {
      return this.generateLabelMarkup(option);
    }

    return '<span>{{ textOption }}</span>';
  }

  private getGeneratedOptionClasses(option: DropdownGeneratorOption) {
    return [
      option.hasDescription ? 'dropdown__item--descriptive' : '',
      option.hasDescriptionBorderBottom ? 'dropdown__item--description-border-bottom' : '',
      option.hasIcon && option.itemColor !== 'danger' && option.iconColor !== this.defaultIconColor ? this.getIconColorClass(option.iconColor) : '',
      option.itemColor !== this.defaultItemColor ? this.getItemColorClass(option.itemColor) : '',
      option.fontWeight !== this.defaultItemFontWeight ? this.getItemFontWeightClass(option.fontWeight) : ''
    ].filter(Boolean);
  }

  private generateOptionOpeningTag(option: DropdownGeneratorOption, classAttribute: string, submenuId?: string) {
    const submenuAttribute = submenuId ? ` submenuToggle="${submenuId}"` : '';

    if (option.elementType === 'anchor') {
      return `<a href="javascript:void(0)"${classAttribute} appDropdownItem${submenuAttribute}>`;
    }

    return `<button type="button"${classAttribute} appDropdownItem${submenuAttribute}>`;
  }

  private generateOptionClosingTag(option: DropdownGeneratorOption) {
    return option.elementType === 'anchor' ? '</a>' : '</button>';
  }

  private getClosedMenuIconMarkup() {
    const iconClasses = this.getClosedMenuIconClasses();

    if (this.closedMenuIcon === 'chevron') {
      return `<i class="${['far fa-chevron-down', 'dropdown__toggle-chevron', iconClasses].filter(Boolean).join(' ')}"></i>`;
    }

    if (this.closedMenuIcon === 'ellipsis') {
      return `<i class="${['far fa-ellipsis-v', iconClasses].filter(Boolean).join(' ')}"></i>`;
    }

    return '';
  }

  private getItemColorClass(itemColor: DropdownItemColor) {
    return `dropdown__item--${itemColor}`;
  }

  private getIconColorClass(iconColor: DropdownIconColor) {
    return `dropdown__item--icon-autentigrey-${iconColor}`;
  }

  private getItemFontWeightClass(fontWeight: DropdownItemFontWeight) {
    return `dropdown__item--font-weight-${fontWeight}`;
  }

  private indent(value: string, spaces: number) {
    const padding = ' '.repeat(spaces);

    return value
      .split('\n')
      .map(line => `${padding}${line}`)
      .join('\n');
  }

  private escapeHtml(value: string) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private keepPreviewOpen() {
    setTimeout(() => {
      this.dropdown?.goBack();
      this.dropdown?.open();
    });
  }
}
